/* eslint-disable prettier/prettier */
import React from 'react'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { showToast } from './toastSlice'
import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import { fetchPortalUsersUrl, addPortalUserUrl, editPortalUserUrl } from 'src/services/urls'
import { getUniqueListBy } from 'src/lib/utils'
import { setSearchBar } from './tableSlice'

const initialState = {
  loading: false,
  data: { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 },
  docs: [],
  error: '',
  activePage: 0,
}

// Fetch all admins
export const fetchPortalUsers = createAsyncThunk(
  'portalUsers/fetchPortalUsers',
  async (options, { dispatch }) => {
    try {
      const skip = options?.skip
      const limit = options?.limit
      const name = options?.userName
      const filter = options?.filter

      // const brandName = options?.brandName
      // const sortBy = options?.sortBy
      // const orderBy = options?.orderBy

      const response = await makeRequest(fetchPortalUsersUrl(skip, limit, name))

      return { docs: response.data.data.docs, data: response.data.data, filtered: name || filter }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

// Create an admin
export const addPortalUser = createAsyncThunk(
  'portalUsers/addPortalUser',
  async (data, { dispatch }) => {
    try {
      let body = data
      const searchUserName = body?.searchUserName
      delete body.searchUserName
      const response = await makeRequest(addPortalUserUrl(), 'POST', body)

      const { displayMessage, statusMessage } = response.data

      if (response.data.statusCode === 201) {
        dispatch(showToast({ title: statusMessage, message: displayMessage, color: 'success' }))
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      dispatch(fetchPortalUsers({ limit: 500, filter: true, userName: searchUserName }))
      dispatch(setSearchBar(searchUserName))
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// Edit/Update or enable/disable an admin
export const editPortalUser = createAsyncThunk(
  'portalUsers/editPortalUser',
  async (data, { dispatch }) => {
    try {
      let body = data
      let searchUserName = body?.searchUserName
      delete body.searchUserName
      const response = await makeRequest(editPortalUserUrl(), 'PUT', body)

      const { displayMessage, status } = response.data
      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }
      dispatch(fetchPortalUsers({ limit: 500, filter: true, userName: searchUserName }))
      dispatch(setSearchBar(searchUserName))
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

const portalUserManagementSlice = createSlice({
  name: 'portalUsers',
  initialState,
  reducers: {
    setActivePage: (state, action) => {
      state.activePage = action.payload
    },
    resetUserManagementList: (state) => {
      state.data = { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 }
      state.docs = []
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPortalUsers.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchPortalUsers.fulfilled, (state, action) => {
      state.loading = false
      state.error = action.payload.error
      if (action.payload.docs) {
        state.data = action.payload.data
        const oldData = action.payload.filtered ? [] : state.docs
        const totalData = [...oldData, ...action.payload.docs]
        const uniqueData = getUniqueListBy(totalData, 'emailId')
        state.docs = uniqueData
      }
    })
    builder.addCase(fetchPortalUsers.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(addPortalUser.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(addPortalUser.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(editPortalUser.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(editPortalUser.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
  },
})

export default portalUserManagementSlice.reducer
export const { setActivePage, resetUserManagementList } = portalUserManagementSlice.actions
